"use client";

import { useEffect, useState, useCallback } from "react";
import DOMPurify from "dompurify";
import {
  Box,
  Image,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  useDisclosure,
  Link,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CommonCheckTable from "common/components/checktable";
import { HasAccess } from "common/utils/accessUtils";
import { accessModuleTitles } from "common/constant";
import { globalStyles } from "common/theme/styles";
import DateFormatter from "common/utils/dateFormater";
import viewIcon from "common/assets/img/viewIcon.png";
import documentVerificationService from "services/Recruit/services/documentVerification";
import { getStatusBadgeStyle } from "common/utils/statusColor";
import { formatStatus } from "services/Crm/utils/calcUtils";
import { getApi } from "common/services/api";
import { API_PATHS } from "common/services/apiPaths";
import TextEditor from "common/components/textEditor/TextEditor";
import emailTemplateService from "services/Recruit/services/emailTemplate";
import CustomDatePicker from "common/components/datePicker/CustomDatePicker";
import {
  getTableFilters,
  saveTableFilters,
  clearTableFilters,
  getInitialFilterState
} from "common/utils/filterUtils";

const DocumentVerification = () => {
  const title = "Document Verification";
  const TABLE_NAME = 'documentVerification'; // Define table name constant
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedValues, setSelectedValues] = useState([]);
  const [displaySearchData, setDisplaySearchData] = useState(false);
  const navigate = useNavigate();
  const [sortColumn, setSortColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchbox, setSearchbox] = useState(() => {
    const filters = getInitialFilterState(TABLE_NAME);
    return filters?.searchbox || "";
  });
  const [finalSearchedData, setFinalSearchedData] = useState([]);
  const [metadata, setMetadata] = useState({});
  const [permission] = HasAccess([accessModuleTitles.Applications]); // Using Applications permission for now
  const { isOpen: isEmailViewOpen, onOpen: onEmailViewOpen, onClose: onEmailViewClose } = useDisclosure();
  const [selectedEmailPreview, setSelectedEmailPreview] = useState(null);

  // Check if user is a reviewer - reviewers should always be able to view
  const getUserRole = () => {
    try {
      const userData = localStorage.getItem('user');
      if (!userData) return null;
      const user = JSON.parse(userData);
      return user?.activeRoleId?.roleName || user?.role || null;
    } catch (error) {
      console.error('Error getting user role:', error);
      return null;
    }
  };
  const isReviewer = getUserRole() === 'reviewer';

  // Create modal state
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const [applications, setApplications] = useState([]);
  const [isApplicationsLoading, setIsApplicationsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [isTemplatesLoading, setIsTemplatesLoading] = useState(false);
  const [createForm, setCreateForm] = useState({
    applicationId: '',
    emailTemplateId: '',
    emailSubject: 'Document Verification Request',
    emailContent: '',
    deadlineDate: '',
  });

  // Status filter options
  const verificationStatusOptions = [
    { value: "not_started", label: "Not Started" },
    { value: "in_progress", label: "In Progress" },
    { value: "pending_review", label: "Pending Review" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "on_hold", label: "On Hold" },
  ];

  const [currentStatusFilter, setCurrentStatusFilter] = useState(() => {
    const filters = getInitialFilterState(TABLE_NAME);
    return filters?.currentStatusFilter || "";
  });

  // Save filters whenever they change
  useEffect(() => {
    const filters = {
      currentStatusFilter,
      searchbox: searchbox || "",
    };
    saveTableFilters(TABLE_NAME, filters);
  }, [currentStatusFilter, searchbox]);

  const handleStatusFilterChange = useCallback((selectedStatus) => {
    setCurrentStatusFilter(selectedStatus);
    setCurrentPage(1);
  }, []);

  // Function to fetch applications eligible for document verification
  const fetchApplications = useCallback(async () => {
    setIsApplicationsLoading(true);
    try {
      // Only fetch applications with interview_cleared status and no existing document verification
      const result = await getApi("api/recruitment/application/eligible-for-document-verification");
      setApplications(result?.data?.data || []);
    } catch (e) {
      console.error("Failed to fetch applications:", e);
      toast.error("Failed to load applications");
    } finally {
      setIsApplicationsLoading(false);
    }
  }, []);

  // Fetch applications and email templates on mount
  useEffect(() => {
    const fetchEmailTemplates = async () => {
      setIsTemplatesLoading(true);
      try {
        const response = await emailTemplateService.getTemplates({ allData: true, isActive: true });
        const templates = response?.data || [];
        setEmailTemplates(templates);
      } catch (e) {
        console.error("Failed to fetch email templates:", e);
        // Don't show error toast, just log it
      } finally {
        setIsTemplatesLoading(false);
      }
    };

    fetchApplications();
    fetchEmailTemplates();
  }, [fetchApplications]);

  // Refetch applications when create modal opens
  useEffect(() => {
    if (isCreateOpen) {
      fetchApplications();
    }
  }, [isCreateOpen, fetchApplications]);

  useEffect(() => {
    fetchData(
      currentPage,
      pageSize,
      sortColumn,
      sortOrder,
      searchbox || "",
      currentStatusFilter || ""
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    pageSize,
    currentStatusFilter,
    sortColumn,
    sortOrder,
  ]);

  const fetchData = async (
    page = currentPage,
    limit = pageSize,
    sortKey = sortColumn,
    order = sortOrder,
    searchTerm = "",
    status = ""
  ) => {
    setIsLoading(true);

    const params = { page, limit };

    if (sortKey) {
      params.sort = order === "desc" ? `-${sortKey}` : sortKey;
    }
    if (searchTerm && searchTerm.trim() !== "") {
      params.search = searchTerm.trim();
    }
    if (status && status.trim() !== "") {
      params.verificationStatus = status.trim();
    }

    try {
      const response = await documentVerificationService.listDocumentVerifications(params);
      const resultData = response?.data?.data ?? [];
      const resultMeta = response?.data?.metadata ?? {};

      const baseIndex = (page - 1) * limit;
      const formattedData = (resultData || []).map((item, index) => {
        // Extract candidate ID safely
        let candidateId = null;
        if (item.candidate?._id) {
          candidateId = typeof item.candidate._id === 'string'
            ? item.candidate._id
            : (item.candidate._id.toString ? item.candidate._id.toString() : String(item.candidate._id));
        } else if (item.candidate && typeof item.candidate === 'string') {
          candidateId = item.candidate;
        } else if (item.candidate && item.candidate.toString) {
          candidateId = item.candidate.toString();
        }

        return {
          _id: item._id,
          srNo: baseIndex + index + 1,
          applicationId: item.application?.customId || "-",
          candidateName: item.candidate?.name || "-",
          candidateEmail: item.candidate?.email || "-",
          candidateId: candidateId, // Store candidate ID for easy access
          verificationStatus: item.verificationStatus || "not_started",
          documentsRequestedAt: item.documentRequestEmail?.sentAt
            ? DateFormatter({ date: item.documentRequestEmail.sentAt })
            : "-",
          lastUpdated: DateFormatter({ date: item.updatedAt || item.createdAt }),
          createdBy:
            `${item.createBy?.firstName || ""} ${item.createBy?.lastName || ""}`.trim() || "-",
          emailSubject:
            item.documentRequestEmail?.subject ||
            item.documentRequestEmail?.emailSubject ||
            item.documentRequestEmail?.subjectLine ||
            "",
          emailContent:
            item.documentRequestEmail?.content ||
            item.documentRequestEmail?.emailContent ||
            item.documentRequestEmail?.body ||
            item.documentRequestEmail?.html ||
            "",
          emailTemplateName:
            item.documentRequestEmail?.templateUsed?.name ||
            item.documentRequestEmail?.emailTemplateId?.name ||
            item.documentRequestEmail?.emailTemplate?.name ||
            item.documentRequestEmail?.templateName ||
            "",
          rawData: item,
          application: item.application,
        };
      });

      setData(formattedData);
      setMetadata(resultMeta);
    } catch (error) {
      toast.error("Failed to fetch document verifications");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSortChange = useCallback((column, order) => {
    setSortColumn(column);
    setSortOrder(order);
    setCurrentPage(1);
  }, []);

  const handleSearchChange = useCallback((searchTerm) => {
    const trimmedTerm = searchTerm?.trim() || "";
    setSearchbox(trimmedTerm);
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      // If already on page 1, manually trigger fetchData
      fetchData(
        1,
        pageSize,
        sortColumn,
        sortOrder,
        trimmedTerm || "",
        currentStatusFilter || ""
      );
    }
  }, [currentPage, pageSize, sortColumn, sortOrder, currentStatusFilter, fetchData]);

  const handleEmailPreviewOpen = (rowData) => {
    setSelectedEmailPreview(rowData);
    onEmailViewOpen();
  };

  const onLinkClick = async (id) => {
    const item = data.find((d) => d._id === id);
    if (!item) {
      console.error('Item not found for id:', id);
      return;
    }

    // Extract Application ID
    let applicationId = null;

    if (item.application && item.application._id) {
      applicationId = item.application._id;
    } else if (item.rawData?.application?._id) {
      applicationId = item.rawData.application._id;
    } else if (item.rawData?.application && typeof item.rawData.application === 'string') {
      applicationId = item.rawData.application;
    }

    if (applicationId) {
      navigate(`/recruitment/application-create/${applicationId}`, {
        state: { activeTab: 2 }, // Document Verification tab index
      });
      return;
    }

    // Fallback: If no Application ID, try to find it via Candidate ID
    console.log('No Application ID found directly. Trying to fetch from Candidate...');

    // Extract Candidate ID
    let candidateId = null;
    if (item.candidateId) {
      candidateId = item.candidateId;
    } else if (item?.rawData?.candidate) {
      if (item.rawData.candidate._id) {
        candidateId = item.rawData.candidate._id;
      } else if (typeof item.rawData.candidate === 'string') {
        candidateId = item.rawData.candidate;
      }
    }

    if (candidateId) {
      // Ensure string
      const candidateIdStr = typeof candidateId === 'string' ? candidateId : (candidateId._id || candidateId.toString() || String(candidateId));

      if (candidateIdStr && candidateIdStr !== '[object Object]') {
        try {
          setIsLoading(true); // Show loading indicator
          const response = await getApi(`${API_PATHS.CANDIDATE_LIST}${candidateIdStr}/applications`);
          const applications = response?.data?.data || [];

          if (applications.length > 0) {
            // Sort by createdAt desc to get the latest
            const latestApp = applications.sort((a, b) =>
              new Date(b.createdAt) - new Date(a.createdAt)
            )[0];

            if (latestApp && latestApp._id) {
              toast.info('Redirecting to the latest application for this candidate.');
              navigate(`/recruitment/application-create/${latestApp._id}`, {
                state: { activeTab: 2 },
              });
            } else {
              toast.warning('Found candidate applications but could not determine ID.');
            }
          } else {
            toast.warning('No applications found for this candidate. Please create an application first.');
          }
        } catch (error) {
          console.error('Error fetching applications for candidate:', error);
          toast.error('Failed to find associated application.');
        } finally {
          setIsLoading(false);
        }
      } else {
        console.error('Invalid candidateId:', candidateId);
        toast.error('Unable to navigate: Invalid candidate ID');
      }
    } else {
      console.error('No Application ID and no Candidate ID found for item:', item);
      toast.error('Unable to navigate: Record data is incomplete');
    }
  };

  // Helper function to replace placeholders in content
  const replacePlaceholders = (content, candidateName, jobRole, deadlineDate) => {
    if (!content) return '';

    const formattedDeadlineDate = deadlineDate
      ? new Date(deadlineDate).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
      : '[Deadline Date]';

    const placeholderMap = {
      '[Candidate Name]': candidateName || '[Candidate Name]',
      '[CANDIDATE_NAME]': candidateName || '[Candidate Name]',
      '{{CANDIDATE_NAME}}': candidateName || '[Candidate Name]',
      '[YourName]': candidateName || '[YourName]',
      '[YOUR_NAME]': candidateName || '[YourName]',
      '{{YOUR_NAME}}': candidateName || '[YourName]',
      '[Job Role]': jobRole || '[Job Role]',
      '[JOB_ROLE]': jobRole || '[Job Role]',
      '{{JOB_TITLE}}': jobRole || '[Job Role]',
      '{{JOB_ROLE}}': jobRole || '[Job Role]',
      '[Deadline Date]': formattedDeadlineDate,
      '[DEADLINE_DATE]': formattedDeadlineDate,
      '{{DEADLINE_DATE}}': formattedDeadlineDate,
    };

    let replacedContent = content;
    Object.entries(placeholderMap).forEach(([placeholder, value]) => {
      const regex = new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
      replacedContent = replacedContent.replace(regex, value);
    });

    return replacedContent;
  };

  // Handle application selection - replace placeholders in existing content
  const handleApplicationSelect = (applicationId) => {
    const selectedApp = applications.find(app => app._id === applicationId);
    if (selectedApp) {
      const candidateName = selectedApp.candidateDetails?.name || '';
      const jobRole = selectedApp.job?.postingTitle || '';

      // Replace placeholders in existing email content if template is already selected
      let updatedContent = createForm.emailContent;
      if (createForm.emailContent) {
        updatedContent = replacePlaceholders(
          createForm.emailContent,
          candidateName,
          jobRole,
          createForm.deadlineDate
        );
      }

      // Replace placeholders in subject too
      let updatedSubject = createForm.emailSubject;
      if (createForm.emailSubject) {
        updatedSubject = replacePlaceholders(
          createForm.emailSubject,
          candidateName,
          jobRole,
          createForm.deadlineDate
        );
      }

      setCreateForm({
        ...createForm,
        applicationId,
        emailContent: updatedContent,
        emailSubject: updatedSubject,
      });
    } else {
      setCreateForm({
        ...createForm,
        applicationId,
      });
    }
  };

  const handleTemplateSelect = (templateId) => {

    if (!templateId) {
      setCreateForm(prev => ({
        ...prev,
        emailTemplateId: '',
        emailSubject: 'Document Verification Request',
        emailContent: '',
        deadlineDate: '',
      }));
      return;
    }

    const template = emailTemplates.find(t => String(t._id) === String(templateId));

    if (!template) {
      console.error("Template not found for ID:", templateId);
      return;
    }

    const selectedApp = applications.find(app => String(app._id) === String(createForm.applicationId));
    const candidateName = selectedApp?.candidateDetails?.name || '';
    const jobRole = selectedApp?.job?.postingTitle || '';

    if (!createForm.applicationId || !candidateName || !jobRole) {
      toast.warning('Please select an application first to replace placeholders automatically');
    }

    const replacedContent = replacePlaceholders(
      template.content || '',
      candidateName,
      jobRole,
      createForm.deadlineDate
    );

    const replacedSubject = replacePlaceholders(
      template.subject || createForm.emailSubject,
      candidateName,
      jobRole,
      createForm.deadlineDate
    );

    console.log('2. About to set state with templateId:', templateId);

    setCreateForm(prev => {
      console.log('3. Inside setCreateForm, prev.emailTemplateId:', prev.emailTemplateId);
      const newState = {
        ...prev,
        emailTemplateId: templateId,
        emailSubject: replacedSubject,
        emailContent: replacedContent,
      };
      console.log('4. New state emailTemplateId:', newState.emailTemplateId);
      return newState;
    });
  };


  const formatJobTitle = (title) => {
    if (!title) return "Unknown";
    return title
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const resetCreateForm = () => {
    setCreateForm({
      applicationId: '',
      emailTemplateId: '',
      emailSubject: 'Document Verification Request',
      emailContent: '',
      deadlineDate: '',
    });
  };

  const handleModalClose = () => {
    resetCreateForm();
    onCreateClose();
  };
  const handleCreateDocumentVerification = async () => {
    if (!createForm.applicationId) {
      toast.error("Please select an application");
      return;
    }
    if (!createForm.emailSubject || !createForm.emailContent) {
      toast.error("Please fill in email subject and content");
      return;
    }

    setIsCreating(true);
    try {
      console.log('[Doc Verification] Sending email with content:', {
        hasContent: !!createForm.emailContent,
        contentLength: createForm.emailContent?.length,
        templateId: createForm.emailTemplateId
      });

      const response = await documentVerificationService.requestDocuments({
        applicationId: createForm.applicationId,
        emailSubject: createForm.emailSubject,
        emailContent: createForm.emailContent,
        requiredDocuments: [],
        emailTemplateId: createForm.emailTemplateId || undefined,
        deadlineDate: createForm.deadlineDate || undefined,
      });

      toast.success(response?.data?.message || "Document verification created successfully");
      onCreateClose();
      setCreateForm({
        applicationId: '',
        emailTemplateId: '',
        emailSubject: 'Document Verification Request',
        emailContent: '',
        deadlineDate: '',
      });
      // Refresh the list
      fetchData(
        currentPage,
        pageSize,
        sortColumn,
        sortOrder,
        searchbox || "",
        currentStatusFilter || ""
      );
    } catch (error) {
      console.error("Error creating document verification:", error);
      toast.error(error.response?.data?.message || error.message || "Failed to create document verification");
    } finally {
      setIsCreating(false);
    }
  };

  const tableColumns = [
    {
      Header: "#",
      accessor: "srNo",
      isSortable: false,
      width: 5,
    },
    {
      Header: "Application ID",
      accessor: "applicationId",
      isSortable: true,
    },
    {
      Header: "Candidate Name",
      accessor: "candidateName",
      isSortable: true,
    },
    {
      Header: "Verification Status",
      accessor: "verificationStatus",
      isSortable: true,
      cell: ({ row }) => {
        const status = row?.values?.verificationStatus || "not_started";
        const badgeStyle = getStatusBadgeStyle(status);

        return (
          <Box display="flex" alignItems="center" gap={2}>
            <Box bg={badgeStyle.color} h="8px" w="8px" borderRadius="full" />
            <Text as="span" color={badgeStyle.color} style={{ color: badgeStyle.color }} fontWeight="500" whiteSpace="nowrap">
              {formatStatus(status)}
            </Text>
          </Box>
        );
      },
    },
    {
      Header: "Documents Requested At",
      accessor: "documentsRequestedAt",
      isSortable: true,
    },
    {
      Header: "Email",
      accessor: "emailSubject",
      isSortable: false,
      cell: ({ row }) => {
        const hasEmail = row?.original?.emailSubject || row?.original?.emailContent || row?.original?.emailTemplateName;
        if (!hasEmail) return <Text>-</Text>;

        return (
          <Link color="blue.500" fontWeight="600" onClick={() => handleEmailPreviewOpen(row.original)}>
            View Email
          </Link>
        );
      },
    },
    {
      Header: "Last Updated",
      accessor: "lastUpdated",
      isSortable: true,
    },
  ];

  const actionHeader = {
    Header: "Actions",
    isSortable: false,
    center: true,
    cell: ({ row }) => (
      <Text fontSize="md" fontWeight="900" textAlign={"center"}>
        <Box className="actionCellContainer">
          {(permission?.view || isReviewer) && (
            <Image
              onClick={() => onLinkClick(row?.original?._id)}
              src={viewIcon}
              h={"20px"}
              w={"20px"}
              cursor="pointer"
              title="View Document Verification"
            />
          )}
        </Box>
      </Text>
    ),
  };

  const dataColumn = [...tableColumns];

  return (
    <div>
      <CommonCheckTable
        title={title}
        showInlineTitle={true}
        isLoading={isLoading}
        columnData={tableColumns}
        dataColumn={dataColumn}
        allData={data}
        onOpen={!isReviewer ? onCreateOpen : undefined} // Hide create button for reviewers
        tableData={data}
        setSearchDisplay={setDisplaySearchData}
        tableCustomFields={[]}
        access={permission}
        action={true}
        setAction={() => { }}
        selectedValues={selectedValues}
        setSelectedValues={setSelectedValues}
        fetchData={fetchData}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        searchboxOutside={searchbox}
        sortOrder={sortOrder}
        searchbox={searchbox}
        setSearchbox={setSearchbox}
        finalSearchedData={finalSearchedData}
        setFinalSearchedData={setFinalSearchedData}
        isIdHide={true}
        metadata={metadata}
        handleSortChange={handleSortChange}
        handleSearchChange={handleSearchChange}
        filterStatusOptions={verificationStatusOptions}
        statusFilter={true}
        handleStatusFilterChange={handleStatusFilterChange}
        sortColumn={sortColumn}
        currentStatusFilter={currentStatusFilter}
        onLinkClick={(id) => { onLinkClick(id); }}
        isDynamic={true}
      />

      {/* Create Document Verification Modal */}
      <Modal isOpen={isCreateOpen} onClose={handleModalClose} size="4xl">
        <ModalOverlay />
        <ModalContent maxW="1200px" w="90vw">
          <ModalHeader>Create Document Verification</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {/* Application - always enabled */}
              <FormControl isRequired>
                <FormLabel>Application</FormLabel>
                <Select
                  placeholder={isApplicationsLoading ? "Loading applications..." : "Select an application"}
                  value={createForm.applicationId}
                  onChange={(e) => handleApplicationSelect(e.target.value)}
                  isDisabled={isApplicationsLoading}
                >
                  {applications
                    .filter((app) =>
                      app?.job?.deleted !== true &&
                      app?.candidateDetails?.deleted !== true
                    )
                    .map((app) => {
                      const candidateName = app.candidateDetails?.name || "Unknown";
                      const jobTitle = formatJobTitle(app.job?.postingTitle) || "Unknown";
                      const appId = app.customId || app._id;
                      return (
                        <option key={app._id} value={app._id}>
                          {candidateName} - {jobTitle} ({appId})
                        </option>
                      );
                    })}
                </Select>
                {!createForm.applicationId && (
                  <Text fontSize="xs" color="orange.500" mt={1}>
                    Please select an application to enable other fields
                  </Text>
                )}
              </FormControl>

              {/* Email Template - disabled until application selected */}
              <FormControl isDisabled={!createForm.applicationId}>
                <FormLabel>Email Template (Optional)</FormLabel>
                <Select
                  value={createForm.emailTemplateId}
                  onChange={(e) => handleTemplateSelect(e.target.value)}
                  isDisabled={isTemplatesLoading || !createForm.applicationId}
                >
                  <option value="">
                    {isTemplatesLoading ? "Loading templates..." : "Select an email template"}
                  </option>
                  {emailTemplates.map((template) => (
                    <option key={String(template._id)} value={String(template._id)}>
                      {template.name} ({template.category || 'custom'})
                    </option>
                  ))}
                </Select>
              </FormControl>

              {/* Email Subject - disabled until application selected */}
              <FormControl isRequired isDisabled={!createForm.applicationId}>
                <FormLabel>Email Subject</FormLabel>
                <Input
                  value={createForm.emailSubject}
                  onChange={(e) => setCreateForm({ ...createForm, emailSubject: e.target.value })}
                  placeholder="Document Verification Request"
                  isDisabled={!createForm.applicationId}
                />
              </FormControl>

              {/* Email Content - disabled until application selected */}
              <FormControl isRequired isDisabled={!createForm.applicationId}>
                <FormLabel>Email Content</FormLabel>
                <TextEditor
                  toolbarId="document-verification-email-content"
                  value={createForm.emailContent || ''}
                  handleChange={(value) => {
                    const cleanedValue = value === "<p><br></p>" ? "" : value;
                    setCreateForm(prev => ({ ...prev, emailContent: cleanedValue }));
                  }}
                  isDisabled={!createForm.applicationId}
                />
                <Text fontSize="xs" color="gray.500" mt={2}>
                  Available placeholders: [Candidate Name], [Job Role], [YourName], [Deadline Date]
                </Text>
              </FormControl>

            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={handleModalClose} isDisabled={isCreating}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleCreateDocumentVerification}
              isLoading={isCreating}
              loadingText="Creating..."
            >
              Create & Send Request
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isEmailViewOpen} onClose={onEmailViewClose} size="4xl">
        <ModalOverlay />
        <ModalContent maxW="1200px" w="90vw" h={"70vh"} overflowY="auto">
          <ModalHeader>Sent Email</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <Text fontSize="sm" color="gray.500">Candidate</Text>
                <Text fontWeight="600">{selectedEmailPreview?.candidateName || "-"}</Text>
              </Box>
              {selectedEmailPreview?.emailTemplateName && (
                <Box>
                  <Text fontSize="sm" color="gray.500">Template</Text>
                  <Text fontWeight="600">{selectedEmailPreview.emailTemplateName}</Text>
                </Box>
              )}
              <Box>
                <Text fontSize="sm" color="gray.500">Subject</Text>
                <Text fontWeight="600">{selectedEmailPreview?.emailSubject || "-"}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500" mb={2}>Email Content</Text>
                {selectedEmailPreview?.emailContent ? (
                  <Box
                    p={4}
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedEmailPreview.emailContent) }}
                  />
                ) : (
                  <Text color="gray.500">No email content available.</Text>
                )}
              </Box>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default DocumentVerification;
